import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaFilePdf,
  FaFileExcel,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmt = (val) =>
  Number(val || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const BalanceSheetComparisonTable = ({ reportData }) => {
  const { years = [], sections = {}, totals = {}, yearly_data = [] } = reportData || {};

  // Accordion state
  const [assetsOpen, setAssetsOpen] = useState(true);
  const [liabilitiesOpen, setLiabilitiesOpen] = useState(true);
  const [equityOpen, setEquityOpen] = useState(true);

  // Subcategory collapse states
  const [currentAssetsOpen, setCurrentAssetsOpen] = useState(true);
  const [fixedAssetsOpen, setFixedAssetsOpen] = useState(true);
  const [currentLiabilitiesOpen, setCurrentLiabilitiesOpen] = useState(true);

  if (!years || years.length === 0) {
    return <div className="alert alert-info">No comparison data available.</div>;
  }

  const latestYear = years[years.length - 1];
  const priorYear = years.length > 1 ? years[years.length - 2] : null;

  const latestAssets = totals?.total_assets?.[latestYear] ?? 0;
  const latestLiabilities = totals?.total_liabilities?.[latestYear] ?? 0;
  const latestEquity = totals?.total_equity?.[latestYear] ?? 0;

  // YoY calculations
  const getYoY = (metricObj) => {
    if (!priorYear || !metricObj) return null;
    const curr = metricObj[latestYear] || 0;
    const prev = metricObj[priorYear] || 0;
    if (prev === 0) return curr !== 0 ? (curr > 0 ? 100 : -100) : 0;
    return Math.round(((curr - prev) / Math.abs(prev)) * 100);
  };

  const assetsYoY = getYoY(totals?.total_assets);
  const liabilitiesYoY = getYoY(totals?.total_liabilities);
  const equityYoY = getYoY(totals?.total_equity);

  // Column styling
  const tdRight = { textAlign: "right", whiteSpace: "nowrap", verticalAlign: "middle" };
  const thRight = { textAlign: "right", whiteSpace: "nowrap" };

  // ── Excel Export ────────────────────────────────────────────────────────
  const exportExcel = () => {
    const rows = [];

    // Header row
    rows.push(["Description", ...years.map((y) => String(y))]);

    // ASSETS
    rows.push(["ASSETS", ...years.map((y) => totals?.total_assets?.[y] || 0)]);
    sections?.assets?.all?.forEach((r) => {
      rows.push([`  ${r.name}`, ...years.map((y) => r.values?.[y] || 0)]);
    });
    rows.push(["TOTAL ASSETS", ...years.map((y) => totals?.total_assets?.[y] || 0)]);
    rows.push([]);

    // LIABILITIES
    rows.push(["LIABILITIES", ...years.map((y) => totals?.total_liabilities?.[y] || 0)]);
    sections?.liabilities?.all?.forEach((r) => {
      rows.push([`  ${r.name}`, ...years.map((y) => r.values?.[y] || 0)]);
    });
    rows.push(["TOTAL LIABILITIES", ...years.map((y) => totals?.total_liabilities?.[y] || 0)]);
    rows.push([]);

    // EQUITY
    rows.push(["EQUITY", ...years.map((y) => totals?.total_equity?.[y] || 0)]);
    sections?.equity?.all?.forEach((r) => {
      rows.push([`  ${r.name}`, ...years.map((y) => r.values?.[y] || 0)]);
    });
    rows.push(["TOTAL EQUITY", ...years.map((y) => totals?.total_equity?.[y] || 0)]);
    rows.push([]);

    // TOTAL LIABILITIES & EQUITY
    rows.push([
      "TOTAL LIABILITIES & EQUITY",
      ...years.map((y) => totals?.total_liabilities_and_equity?.[y] || 0),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Sheet Comparison");
    XLSX.writeFile(workbook, `Balance_Sheet_Comparison_${years.length}_Years.xlsx`);
  };

  // ── PDF Export ──────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(16);
    doc.setTextColor(13, 110, 253);
    doc.text("Rekonic - Multi-Year Balance Sheet Comparison", 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Comparison Period: ${years[0]} - ${latestYear} (${years.length} Years) | Generated on ${new Date().toLocaleDateString()}`,
      14,
      25
    );

    const headers = [["Line Item", ...years.map((y) => String(y))]];
    const body = [];

    // Assets
    body.push([{ content: "ASSETS", colSpan: years.length + 1, styles: { fontStyle: "bold", fillColor: [231, 241, 255] } }]);
    sections?.assets?.all?.forEach((r) => {
      body.push([`  ${r.name}`, ...years.map((y) => `$${fmt(r.values?.[y])}`)]);
    });
    body.push([
      { content: "Total Assets", styles: { fontStyle: "bold" } },
      ...years.map((y) => `$${fmt(totals?.total_assets?.[y])}`),
    ]);

    // Liabilities
    body.push([{ content: "LIABILITIES", colSpan: years.length + 1, styles: { fontStyle: "bold", fillColor: [255, 244, 229] } }]);
    sections?.liabilities?.all?.forEach((r) => {
      body.push([`  ${r.name}`, ...years.map((y) => `$${fmt(r.values?.[y])}`)]);
    });
    body.push([
      { content: "Total Liabilities", styles: { fontStyle: "bold" } },
      ...years.map((y) => `$${fmt(totals?.total_liabilities?.[y])}`),
    ]);

    // Equity
    body.push([{ content: "EQUITY", colSpan: years.length + 1, styles: { fontStyle: "bold", fillColor: [230, 244, 234] } }]);
    sections?.equity?.all?.forEach((r) => {
      body.push([`  ${r.name}`, ...years.map((y) => `$${fmt(r.values?.[y])}`)]);
    });
    body.push([
      { content: "Total Equity", styles: { fontStyle: "bold" } },
      ...years.map((y) => `$${fmt(totals?.total_equity?.[y])}`),
    ]);

    // Total L&E
    body.push([
      { content: "TOTAL LIABILITIES & EQUITY", styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      ...years.map((y) => `$${fmt(totals?.total_liabilities_and_equity?.[y])}`),
    ]);

    autoTable(doc, {
      head: headers,
      body: body,
      startY: 30,
      theme: "striped",
      headStyles: { fillColor: [13, 110, 253], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: "auto" },
      },
    });

    doc.save(`Balance_Sheet_Comparison_${years.length}_Years.pdf`);
  };

  return (
    <div>
      {/* ── KPI Summary Cards ────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {/* Total Assets */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border-start border-primary border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted fw-semibold small">TOTAL ASSETS ({latestYear})</span>
                {assetsYoY !== null && (
                  <span
                    className={`badge ${
                      assetsYoY >= 0 ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"
                    }`}
                  >
                    {assetsYoY >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(assetsYoY)}% YoY
                  </span>
                )}
              </div>
              <h3 className="mt-2 mb-0 fw-bold text-primary">${fmt(latestAssets)}</h3>
              <small className="text-muted">
                {priorYear ? `vs $${fmt(totals?.total_assets?.[priorYear])} in ${priorYear}` : "Current fiscal position"}
              </small>
            </div>
          </div>
        </div>

        {/* Total Liabilities */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border-start border-warning border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted fw-semibold small">TOTAL LIABILITIES ({latestYear})</span>
                {liabilitiesYoY !== null && (
                  <span
                    className={`badge ${
                      liabilitiesYoY <= 0 ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"
                    }`}
                  >
                    {liabilitiesYoY >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(liabilitiesYoY)}% YoY
                  </span>
                )}
              </div>
              <h3 className="mt-2 mb-0 fw-bold text-warning">${fmt(latestLiabilities)}</h3>
              <small className="text-muted">
                {priorYear ? `vs $${fmt(totals?.total_liabilities?.[priorYear])} in ${priorYear}` : "Obligations & payables"}
              </small>
            </div>
          </div>
        </div>

        {/* Total Equity */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border-start border-success border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted fw-semibold small">TOTAL EQUITY ({latestYear})</span>
                <span className="badge bg-success text-white">
                  <FaCheckCircle className="me-1" /> Balanced
                </span>
              </div>
              <h3 className="mt-2 mb-0 fw-bold text-success">${fmt(latestEquity)}</h3>
              <small className="text-muted">
                {priorYear ? `vs $${fmt(totals?.total_equity?.[priorYear])} in ${priorYear}` : "Net company worth"}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* ── Table Card ───────────────────────────────────────────────────── */}
      <div className="card shadow-sm mb-4">
        {/* Card Header with Exports */}
        <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
          <div>
            <h5 className="mb-0 fw-bold text-dark">
              Balance Sheet Statement — Side-by-Side Comparison
            </h5>
            <small className="text-muted">
              Annual comparison over {years.length} years ({years[0]} – {latestYear})
            </small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" onClick={exportPDF}>
              <FaFilePdf /> Export PDF
            </button>
            <button className="btn btn-outline-success btn-sm d-flex align-items-center gap-1" onClick={exportExcel}>
              <FaFileExcel /> Export Excel
            </button>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-bordered mb-0 align-middle">
              <thead className="table-dark">
                <tr>
                  <th style={{ minWidth: 280 }}>Line Item / Account Description</th>
                  {years.map((y) => (
                    <th key={y} style={{ ...thRight, minWidth: 140 }}>
                      FY {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ════════════════════════════════════════════════════════════
                    1. ASSETS
                ════════════════════════════════════════════════════════════ */}
                <tr
                  className="table-primary"
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                  onClick={() => setAssetsOpen(!assetsOpen)}
                >
                  <td>
                    {assetsOpen ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    ASSETS
                  </td>
                  {years.map((y) => (
                    <td key={y} style={tdRight}>
                      ${fmt(totals?.total_assets?.[y])}
                    </td>
                  ))}
                </tr>

                {assetsOpen && (
                  <>
                    {/* Current Assets Category */}
                    <tr
                      style={{ cursor: "pointer", background: "#f8f9fa", fontWeight: 600 }}
                      onClick={() => setCurrentAssetsOpen(!currentAssetsOpen)}
                    >
                      <td style={{ paddingLeft: 28 }}>
                        {currentAssetsOpen ? <FaChevronDown className="me-2 text-muted" /> : <FaChevronRight className="me-2 text-muted" />}
                        Current Assets
                      </td>
                      {years.map((y) => (
                        <td key={y} style={tdRight}>
                          ${fmt(
                            sections?.assets?.current_assets?.reduce(
                              (s, r) => s + (r.values?.[y] || 0),
                              0
                            )
                          )}
                        </td>
                      ))}
                    </tr>

                    {currentAssetsOpen &&
                      sections?.assets?.current_assets?.map((row, idx) => (
                        <tr key={`ca-${idx}`} className="table-light">
                          <td style={{ paddingLeft: 48 }}>{row.name}</td>
                          {years.map((y) => (
                            <td key={y} style={tdRight}>
                              ${fmt(row.values?.[y])}
                            </td>
                          ))}
                        </tr>
                      ))}

                    {/* Fixed Assets Category (if any) */}
                    {sections?.assets?.fixed_assets && sections.assets.fixed_assets.length > 0 && (
                      <>
                        <tr
                          style={{ cursor: "pointer", background: "#f8f9fa", fontWeight: 600 }}
                          onClick={() => setFixedAssetsOpen(!fixedAssetsOpen)}
                        >
                          <td style={{ paddingLeft: 28 }}>
                            {fixedAssetsOpen ? <FaChevronDown className="me-2 text-muted" /> : <FaChevronRight className="me-2 text-muted" />}
                            Fixed / Non-Current Assets
                          </td>
                          {years.map((y) => (
                            <td key={y} style={tdRight}>
                              ${fmt(
                                sections?.assets?.fixed_assets?.reduce(
                                  (s, r) => s + (r.values?.[y] || 0),
                                  0
                                )
                              )}
                            </td>
                          ))}
                        </tr>
                        {fixedAssetsOpen &&
                          sections.assets.fixed_assets.map((row, idx) => (
                            <tr key={`fa-${idx}`} className="table-light">
                              <td style={{ paddingLeft: 48 }}>{row.name}</td>
                              {years.map((y) => (
                                <td key={y} style={tdRight}>
                                  ${fmt(row.values?.[y])}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </>
                    )}

                    {/* Subtotal: Total Assets */}
                    <tr className="fw-bold" style={{ background: "#e7f1ff" }}>
                      <td style={{ paddingLeft: 20 }}>TOTAL ASSETS</td>
                      {years.map((y) => (
                        <td key={y} style={{ ...tdRight, color: "#0d6efd" }}>
                          ${fmt(totals?.total_assets?.[y])}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* ════════════════════════════════════════════════════════════
                    2. LIABILITIES
                ════════════════════════════════════════════════════════════ */}
                <tr
                  className="table-warning"
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                  onClick={() => setLiabilitiesOpen(!liabilitiesOpen)}
                >
                  <td>
                    {liabilitiesOpen ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    LIABILITIES
                  </td>
                  {years.map((y) => (
                    <td key={y} style={tdRight}>
                      ${fmt(totals?.total_liabilities?.[y])}
                    </td>
                  ))}
                </tr>

                {liabilitiesOpen && (
                  <>
                    <tr
                      style={{ cursor: "pointer", background: "#f8f9fa", fontWeight: 600 }}
                      onClick={() => setCurrentLiabilitiesOpen(!currentLiabilitiesOpen)}
                    >
                      <td style={{ paddingLeft: 28 }}>
                        {currentLiabilitiesOpen ? <FaChevronDown className="me-2 text-muted" /> : <FaChevronRight className="me-2 text-muted" />}
                        Current Liabilities
                      </td>
                      {years.map((y) => (
                        <td key={y} style={tdRight}>
                          ${fmt(
                            sections?.liabilities?.current_liabilities?.reduce(
                              (s, r) => s + (r.values?.[y] || 0),
                              0
                            )
                          )}
                        </td>
                      ))}
                    </tr>

                    {currentLiabilitiesOpen &&
                      sections?.liabilities?.current_liabilities?.map((row, idx) => (
                        <tr key={`cl-${idx}`} className="table-light">
                          <td style={{ paddingLeft: 48 }}>{row.name}</td>
                          {years.map((y) => (
                            <td key={y} style={tdRight}>
                              ${fmt(row.values?.[y])}
                            </td>
                          ))}
                        </tr>
                      ))}

                    {/* Subtotal: Total Liabilities */}
                    <tr className="fw-bold" style={{ background: "#fff4e5" }}>
                      <td style={{ paddingLeft: 20 }}>TOTAL LIABILITIES</td>
                      {years.map((y) => (
                        <td key={y} style={{ ...tdRight, color: "#fd7e14" }}>
                          ${fmt(totals?.total_liabilities?.[y])}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* ════════════════════════════════════════════════════════════
                    3. EQUITY
                ════════════════════════════════════════════════════════════ */}
                <tr
                  className="table-success"
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                  onClick={() => setEquityOpen(!equityOpen)}
                >
                  <td>
                    {equityOpen ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    EQUITY
                  </td>
                  {years.map((y) => (
                    <td key={y} style={tdRight}>
                      ${fmt(totals?.total_equity?.[y])}
                    </td>
                  ))}
                </tr>

                {equityOpen && (
                  <>
                    {sections?.equity?.all?.map((row, idx) => (
                      <tr key={`eq-${idx}`} className="table-light">
                        <td style={{ paddingLeft: 48 }}>{row.name}</td>
                        {years.map((y) => (
                          <td key={y} style={tdRight}>
                            ${fmt(row.values?.[y])}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {/* Subtotal: Total Equity */}
                    <tr className="fw-bold" style={{ background: "#e6f4ea" }}>
                      <td style={{ paddingLeft: 20 }}>TOTAL EQUITY</td>
                      {years.map((y) => (
                        <td key={y} style={{ ...tdRight, color: "#198754" }}>
                          ${fmt(totals?.total_equity?.[y])}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* ════════════════════════════════════════════════════════════
                    4. TOTAL LIABILITIES & EQUITY
                ════════════════════════════════════════════════════════════ */}
                <tr
                  style={{
                    background: "#212529",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "1.05rem",
                  }}
                >
                  <td>
                    <FaCheckCircle className="me-2 text-success" />
                    TOTAL LIABILITIES &amp; EQUITY
                  </td>
                  {years.map((y) => (
                    <td key={y} style={{ ...tdRight, color: "#20c997" }}>
                      ${fmt(totals?.total_liabilities_and_equity?.[y])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetComparisonTable;
